import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  SignOut, Briefcase, CurrencyDollar, User, EnvelopeSimple, Phone,
  Clock, MapPin, CheckCircle, WarningCircle, FileText, Calendar, Buildings,
  Check, X
} from "@phosphor-icons/react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";
const portalApi = axios.create({ baseURL: API, withCredentials: true });

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};
const fmtDuration = (h, m) => {
  if (h >= 1) return `${h.toFixed(h % 1 === 0 ? 0 : 1)} hr${h === 1 ? "" : "s"}`;
  return `${m} min`;
};

const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  confirmed: { label: "Confirmed", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress: { label: "In progress", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function PortalDashboard() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const meQ = useQuery({
    queryKey: ["portal", "me"],
    queryFn: async () => (await portalApi.get("/portal/me")).data,
    retry: false,
  });

  const asgQ = useQuery({
    queryKey: ["portal", "assignments"],
    queryFn: async () => (await portalApi.get("/portal/assignments")).data,
    enabled: meQ.isSuccess,
  });

  const sumQ = useQuery({
    queryKey: ["portal", "summary"],
    queryFn: async () => (await portalApi.get("/portal/summary")).data,
    enabled: meQ.isSuccess,
  });

  const logout = useMutation({
    mutationFn: () => portalApi.post("/portal/logout"),
    onSuccess: () => {
      qc.clear();
      navigate("/portal/login", { replace: true });
    },
  });

  const respondAssignment = useMutation({
    mutationFn: ({ projectId, decision, note }) =>
      portalApi.post(`/portal/assignments/${projectId}/respond`, { decision, note }),
    onSuccess: (_r, vars) => {
      toast.success(vars.decision === "accept" ? "Assignment accepted" : "Assignment declined");
      qc.invalidateQueries({ queryKey: ["portal", "assignments"] });
      qc.invalidateQueries({ queryKey: ["portal", "summary"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update assignment"),
  });

  const setAvailability = useMutation({
    mutationFn: (payload) => portalApi.post("/portal/me/availability", payload),
    onSuccess: () => {
      toast.success("Availability updated");
      qc.invalidateQueries({ queryKey: ["portal", "me"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update availability"),
  });

  if (meQ.isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
  }
  if (meQ.isError) {
    navigate("/portal/login", { replace: true });
    return null;
  }

  const me = meQ.data;
  const rows = asgQ.data || [];
  const summary = sumQ.data || {};
  const upcoming = rows.filter((r) => ["pending", "confirmed", "in_progress"].includes(r.status));
  const completed = rows.filter((r) => r.status === "completed");

  const firstName = (me.full_name || "").split(" ")[0] || "there";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-dashboard-page">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Unity Language Group" className="h-8 w-auto" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-bold">Unity Language Group</div>
              <div className="text-xs text-slate-500">Interpreter portal</div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="portal-logout"
            onClick={() => logout.mutate()}
            className="text-slate-600"
          >
            <SignOut size={14} className="mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Greeting */}
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Welcome</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="portal-greeting">
            Hi, {firstName}
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            {me.email} · {(me.language_pairs || []).join(", ") || "—"}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KPI icon={<Briefcase size={18} />} label="Upcoming" value={summary.upcoming_count ?? 0} testid="portal-kpi-upcoming" />
          <KPI icon={<CheckCircle size={18} />} label="Completed" value={summary.completed_count ?? 0} testid="portal-kpi-completed" />
          <KPI icon={<Clock size={18} />} label="Hours (paid)" value={(summary.total_hours ?? 0).toFixed(1)} testid="portal-kpi-hours" />
          <KPI icon={<CurrencyDollar size={18} />} label="Total earned" value={fmtMoney(summary.total_earned)} accent="emerald" testid="portal-kpi-earned" />
        </div>

        {/* Contract status */}
        <div className="card-flat bg-white border border-slate-200 rounded-xl p-5 mb-6 flex items-center justify-between" data-testid="portal-contract-card">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${me.contract_signed_at ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
              {me.contract_signed_at ? <CheckCircle size={20} weight="fill" /> : <WarningCircle size={20} weight="fill" />}
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">1099 Contractor Agreement</div>
              <div className="text-xs text-slate-500">
                {me.contract_signed_at
                  ? <>Signed on <span className="font-mono">{fmtDate(me.contract_signed_at)}</span></>
                  : "Not signed yet — Unity Language Group will email you a signing link."}
              </div>
            </div>
          </div>
        </div>

        {summary.requires_1099 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-xs text-amber-900">
            <b>1099-NEC threshold reached.</b> You've earned over $600 this year. Unity Language Group will issue you a Form 1099-NEC at year end.
          </div>
        )}

        <Tabs defaultValue="upcoming" data-testid="portal-tabs">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="upcoming" data-testid="portal-tab-upcoming">
              <Calendar size={14} className="mr-1.5" /> Upcoming · {upcoming.length}
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="portal-tab-history">
              <Briefcase size={14} className="mr-1.5" /> History · {completed.length}
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="portal-tab-profile">
              <User size={14} className="mr-1.5" /> Profile
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-4">
            {upcoming.length === 0 ? (
              <EmptyState text="No upcoming assignments. You'll see new ones here when they're scheduled." />
            ) : (
              <div className="space-y-3">
                {upcoming.map((a) => (
                  <AssignmentCard
                    key={a.project_id}
                    a={a}
                    onRespond={(decision, note) => respondAssignment.mutate({ projectId: a.project_id, decision, note })}
                    responding={respondAssignment.isPending}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            {completed.length === 0 ? (
              <EmptyState text="No completed assignments yet." />
            ) : (
              <div className="space-y-3">
                {completed.map((a) => <AssignmentCard key={a.project_id} a={a} showEarned />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile" className="mt-4">
            <ProfilePanel me={me} onSetAvailability={(v) => setAvailability.mutate({ availability: v })} savingAvailability={setAvailability.isPending} />
          </TabsContent>
        </Tabs>

        <div className="text-center text-[11px] text-slate-400 mt-10">
          © {new Date().getFullYear()} Unity Language Group. Questions? <a href="mailto:ops@unitylanguagegroup.com" className="underline">ops@unitylanguagegroup.com</a>
        </div>
      </main>
    </div>
  );
}

function KPI({ icon, label, value, accent = "slate", testid }) {
  const tones = { emerald: "text-emerald-700", slate: "text-slate-900" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
        {icon}{label}
      </div>
      <div className={`font-display text-2xl font-bold mt-2 ${tones[accent]}`}>{value}</div>
    </div>
  );
}

function AssignmentCard({ a, showEarned, onRespond, responding }) {
  const st = STATUS[a.status] || STATUS.pending;
  const canRespond = !!onRespond && ["pending", "confirmed"].includes(a.status);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`portal-assignment-${a.project_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{a.project_type || "assignment"}</span>
            <span className="text-[10px] text-slate-400">·</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{a.language || "—"}</span>
          </div>
          <div className="font-semibold text-slate-900 truncate">{a.title || "Untitled"}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-xl font-bold text-slate-900">
            {showEarned ? fmtMoney(a.estimated_payout) : fmtMoney(a.interpreter_rate)}
            {!showEarned && <span className="text-xs text-slate-400 font-normal ml-1">/hr</span>}
          </div>
          <div className="text-[11px] text-slate-500">
            {showEarned ? "You earned" : "Your rate"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 text-xs">
        <Info icon={<Calendar size={12} />} label="When" value={fmtDate(a.scheduled_at)} />
        <Info icon={<Clock size={12} />} label="Duration" value={fmtDuration(a.duration_hours, a.duration_minutes)} />
        <Info icon={<MapPin size={12} />} label="Location" value={a.location || "Remote / TBD"} />
      </div>

      {a.client && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">Contact person</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-slate-700">
              <Buildings size={12} className="text-slate-400" />
              <span>{a.client.name}{a.client.org_type ? ` · ${a.client.org_type}` : ""}</span>
            </div>
            {a.client.contact_name && (
              <div className="flex items-center gap-1.5 text-slate-700">
                <User size={12} className="text-slate-400" />
                <span>{a.client.contact_name}</span>
              </div>
            )}
            {a.client.contact_email && (
              <a href={`mailto:${a.client.contact_email}`} className="flex items-center gap-1.5 text-blue-700 hover:underline">
                <EnvelopeSimple size={12} />
                <span>{a.client.contact_email}</span>
              </a>
            )}
            {a.client.contact_phone && (
              <a href={`tel:${a.client.contact_phone}`} className="flex items-center gap-1.5 text-blue-700 hover:underline">
                <Phone size={12} />
                <span>{a.client.contact_phone}</span>
              </a>
            )}
          </div>
        </div>
      )}

      {a.notes && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Terms / notes</div>
          <div className="text-xs text-slate-700 whitespace-pre-wrap">{a.notes}</div>
        </div>
      )}

      {canRespond && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
          <div className="text-[11px] text-slate-500 mr-auto">
            {a.status === "confirmed" ? "Already confirmed — you can still decline if needed." : "Please respond so Unity can plan."}
          </div>
          <Button
            size="sm"
            disabled={responding || a.status === "confirmed"}
            onClick={() => onRespond("accept")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`portal-accept-${a.project_id}`}
          >
            <Check size={14} className="mr-1" /> {a.status === "confirmed" ? "Accepted" : "Accept"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={responding}
            onClick={() => {
              const note = window.prompt("Optional — reason for declining (leave blank to skip):", "");
              if (note === null) return;
              onRespond("decline", note || undefined);
            }}
            data-testid={`portal-decline-${a.project_id}`}
          >
            <X size={14} className="mr-1" /> Decline
          </Button>
        </div>
      )}
    </div>
  );
}

function Info({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
        {icon}{label}
      </div>
      <div className="text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function ProfilePanel({ me, onSetAvailability, savingAvailability }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="portal-profile">
      <div className="mb-6 pb-6 border-b border-slate-100">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">My availability status</div>
        <div className="flex flex-wrap gap-2">
          {[
            { v: "available", label: "Available", cls: "bg-emerald-600 hover:bg-emerald-700 text-white", inactive: "border-emerald-200 text-emerald-700 hover:bg-emerald-50" },
            { v: "busy", label: "Busy", cls: "bg-amber-500 hover:bg-amber-600 text-white", inactive: "border-amber-200 text-amber-700 hover:bg-amber-50" },
            { v: "off", label: "Off / Not accepting", cls: "bg-slate-600 hover:bg-slate-700 text-white", inactive: "border-slate-200 text-slate-600 hover:bg-slate-50" },
          ].map((o) => {
            const active = me.availability === o.v;
            return (
              <Button
                key={o.v}
                size="sm"
                disabled={savingAvailability || active}
                onClick={() => onSetAvailability?.(o.v)}
                className={active ? o.cls : `bg-white border ${o.inactive}`}
                data-testid={`portal-availability-${o.v}`}
              >
                {o.label}
              </Button>
            );
          })}
        </div>
        <div className="text-[11px] text-slate-500 mt-3">
          Set yourself as <b>Off</b> when unavailable — Unity will not send new assignments until you switch back.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ProfileRow label="Full name" value={me.full_name} />
        <ProfileRow label="Email" value={me.email} />
        <ProfileRow label="Phone" value={me.phone} />
        <ProfileRow label="Availability" value={me.availability} />
        <ProfileRow label="Location" value={me.city_state_zip} />
        <ProfileRow label="Max travel distance" value={me.max_travel_miles != null ? `${me.max_travel_miles} mi` : "Remote only"} />
        <ProfileRow label="Language pairs" value={(me.language_pairs || []).join(", ")} />
        <ProfileRow label="Specialties" value={(me.specialties || []).join(", ")} />
      </div>

      <div className="mt-6 pt-6 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">Your rates</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Rate label="In-person" value={me.hourly_rate} unit="/hr" />
          <Rate label="OPI (phone)" value={me.opi_rate} unit="/hr" />
          <Rate label="VRI (video)" value={me.vri_rate} unit="/hr" />
          <Rate label="Translation" value={me.translation_rate} unit="/word" precision={4} />
        </div>
        <div className="text-[11px] text-slate-400 mt-4">
          To update rates or contact info, email <a href="mailto:ops@unitylanguagegroup.com" className="underline">ops@unitylanguagegroup.com</a>.
        </div>
      </div>
    </div>
  );
}

function ProfileRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-900 mt-1">{value || "—"}</div>
    </div>
  );
}

function Rate({ label, value, unit, precision = 2 }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-lg font-bold text-slate-900 mt-1">
        ${(Number(value) || 0).toFixed(precision)}<span className="text-xs text-slate-400 font-normal ml-1">{unit}</span>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="bg-white border border-dashed border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400">
      {text}
    </div>
  );
}
